import { useState, useEffect } from 'react';
import { SectionWrapper } from '../SectionWrapper';
import { Store, Plus, Trash2, Pencil } from 'lucide-react';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getCurrentUserCompanyId } from '@/lib/userUtils';

interface Vendor {
  id?: string;
  name: string;
  category: 'COGS' | 'OPEX' | 'Payroll' | 'Rent' | string;
  contact_name: string;
  email: string;
  phone: string;
}

interface VendorSetupSectionProps {
  onNext?: () => void | Promise<void>;
  onSkip?: () => void | Promise<void>;
}

export const VendorSetupSection = ({ onNext: externalOnNext, onSkip: externalOnSkip }: VendorSetupSectionProps = {}) => {
  const { completeSection, skipSection, updateSectionData } = useOnboarding();
  const { toast } = useToast();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [customCategory, setCustomCategory] = useState('');
  const [showCustomCategoryInput, setShowCustomCategoryInput] = useState(false);
  const [editingCustomCategory, setEditingCustomCategory] = useState('');
  const [showEditCustomCategoryInput, setShowEditCustomCategoryInput] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newVendor, setNewVendor] = useState<Vendor>({
    name: '',
    category: 'COGS',
    contact_name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await loadVendors();
      setLoading(false);
    };
    loadData();
  }, []);

  const loadVendors = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const { data, error } = await supabase
        .from('vendors')
        .select('id, name, category, contact_name, email, phone')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Error loading vendors:', error);
        throw error;
      }
      
      const mappedData = (data || []).map((v: any) => ({
        ...v,
        name: v.name || '',
        contact_name: v.contact_name || '',
      }));
      
      setVendors(mappedData as Vendor[]);
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const addVendor = async () => {
    if (!newVendor.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Vendor name is required',
        variant: 'destructive'
      });
      return;
    }

    if (!newVendor.category.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Category is required',
        variant: 'destructive'
      });
      return;
    }

    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        toast({
          title: 'Error',
          description: 'Company ID not found. Please complete Business Profile first.',
          variant: 'destructive'
        });
        return;
      }

      // Check if vendor with same name already exists for this company
      const { data: existingVendorData } = await supabase
        .from('vendors')
        .select('id')
        .eq('company_id', companyId)
        .eq('name', newVendor.name)
        .maybeSingle();
      
      const existingVendor = existingVendorData;

      if (existingVendor) {
        toast({
          title: 'Vendor Already Exists',
          description: 'A vendor with this name already exists. Please use a different name.',
          variant: 'destructive'
        });
        return;
      }

      // Prepare vendor data for insertion
      const vendorData: any = {
        name: newVendor.name,
        category: newVendor.category || 'COGS',
        company_id: companyId
      };
      
      // Add optional fields only if they have values
      if (newVendor.contact_name) {
        vendorData.contact_name = newVendor.contact_name;
      }
      if (newVendor.email) {
        vendorData.email = newVendor.email;
      }
      if (newVendor.phone) {
        vendorData.phone = newVendor.phone;
      }

      // Insert vendor into database
      const { data: insertedVendor, error } = await supabase
        .from('vendors')
        .insert(vendorData)
        .select('id, name, category, contact_name, email, phone')
        .single();

      if (error) {
        console.error('Error inserting vendor:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to save vendor',
          variant: 'destructive'
        });
        return;
      }

      // Add vendor to local state with the ID from database
      const vendorWithId = {
        ...newVendor,
        id: insertedVendor.id,
        name: insertedVendor.name || newVendor.name,
      };

      setVendors([...vendors, vendorWithId]);
      
      toast({
        title: 'Success',
        description: 'Vendor added successfully'
      });

      // Reset form
    setNewVendor({
      name: '',
      category: 'COGS',
      contact_name: '',
      email: '',
      phone: ''
    });
    setCustomCategory('');
    setShowCustomCategoryInput(false);
    setShowForm(false);
    } catch (error: any) {
      console.error('Error adding vendor:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to add vendor',
        variant: 'destructive'
      });
    }
  };

  const removeVendor = async (index: number) => {
    const vendor = vendors[index];
    
    // If vendor has an ID, delete it from database
    if (vendor.id) {
      try {
        const { error } = await supabase
          .from('vendors')
          .delete()
          .eq('id', vendor.id);

        if (error) {
          console.error('Error deleting vendor:', error);
          toast({
            title: 'Error',
            description: 'Failed to delete vendor from database',
            variant: 'destructive'
          });
          return;
        }
      } catch (error: any) {
        console.error('Error deleting vendor:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to delete vendor',
          variant: 'destructive'
        });
        return;
      }
    }
    
    // Remove from local state
    setVendors(vendors.filter((_, i) => i !== index));
    
    toast({
      title: 'Vendor Removed',
      description: 'Vendor has been deleted'
    });
  };

  const openEditModal = (vendor: Vendor) => {
    setEditingVendor({ ...vendor });
    // Check if vendor has a custom category (not in standard list)
    const standardCategories = ['COGS', 'OPEX', 'Payroll', 'Rent'];
    if (vendor.category && !standardCategories.includes(vendor.category)) {
      setShowEditCustomCategoryInput(true);
      setEditingCustomCategory(vendor.category);
    } else {
      setShowEditCustomCategoryInput(false);
      setEditingCustomCategory('');
    }
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingVendor(null);
    setShowEditCustomCategoryInput(false);
    setEditingCustomCategory('');
  };

  const saveEditedVendor = async () => {
    if (!editingVendor) return;

    if (!editingVendor.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Vendor name is required',
        variant: 'destructive'
      });
      return;
    }

    if (!editingVendor.category.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Category is required',
        variant: 'destructive'
      });
      return;
    }

    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        toast({
          title: 'Error',
          description: 'Company ID not found',
          variant: 'destructive'
        });
        return;
      }

      if (editingVendor.id) {
        // Update existing vendor in database
        const updateData: any = {
          name: editingVendor.name,
          category: editingVendor.category,
        };
        
        // Add optional fields only if they have values
        if (editingVendor.contact_name) {
          updateData.contact_name = editingVendor.contact_name;
        }
        if (editingVendor.email) {
          updateData.email = editingVendor.email;
        }
        if (editingVendor.phone) {
          updateData.phone = editingVendor.phone;
        }
        
        const { error } = await supabase
          .from('vendors')
          .update(updateData)
          .eq('id', editingVendor.id);

        if (error) {
          console.error('Error updating vendor:', error);
          toast({
            title: 'Error',
            description: 'Failed to update vendor',
            variant: 'destructive'
          });
          return;
        }

        // Update local state
        setVendors(prev => 
          prev.map(v => v.id === editingVendor.id ? editingVendor : v)
        );

        toast({
          title: 'Success',
          description: 'Vendor updated successfully'
        });
      }

      closeEditModal();
      // Reload vendors to get fresh data
      await loadVendors();
    } catch (error: any) {
      console.error('Error saving edited vendor:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save changes',
        variant: 'destructive'
      });
    }
  };

  const handleNext = async () => {
    if (vendors.length === 0) {
      toast({
        title: 'No Vendors Added',
        description: 'Please add at least one vendor or skip this section',
        variant: 'destructive'
      });
      return;
    }

    try {
      const companyId = await getCurrentUserCompanyId();

      if (!companyId) {
        toast({
          title: 'Error',
          description: 'Company ID not found. Please complete Business Profile first.',
          variant: 'destructive'
        });
        return;
      }

      console.log('💾 Verifying vendors:', { companyId, vendorCount: vendors.length });

      // Since vendors are now saved immediately when added, we just need to verify
      // and handle any vendors that might have been edited locally (though they should all have IDs)
      const vendorsWithId = vendors.filter(v => v.id);
      const newVendors = vendors.filter(v => !v.id);
      const updatedVendors = [...vendorsWithId];

        // Update existing vendors
        for (const vendor of vendorsWithId) {
          const updateData: any = {
            name: vendor.name,
            category: vendor.category,
          };
          
          // Add optional fields only if they have values
          if (vendor.contact_name) {
            updateData.contact_name = vendor.contact_name;
          }
          if (vendor.email) {
            updateData.email = vendor.email;
          }
          if (vendor.phone) {
            updateData.phone = vendor.phone;
          }
          
          const { error } = await supabase
            .from('vendors')
            .update(updateData)
            .eq('id', vendor.id);

        if (error) {
          console.error('Error updating vendor:', error);
          throw error;
        }
      }

      // Handle any vendors that weren't saved yet (shouldn't happen with immediate save, but just in case)
      if (newVendors.length > 0) {
        const vendorsToInsert = [];

        for (const vendor of newVendors) {
          // Check if vendor with same name already exists for this company
          const { data: existingVendor } = await supabase
            .from('vendors')
            .select('id')
            .eq('company_id', companyId)
            .eq('name', vendor.name)
            .maybeSingle();

          if (existingVendor) {
            // Update existing instead of inserting
            const updateData: any = {
              name: vendor.name,
              category: vendor.category,
            };
            
            // Add optional fields only if they have values
            if (vendor.contact_name) {
              updateData.contact_name = vendor.contact_name;
            }
            if (vendor.email) {
              updateData.email = vendor.email;
            }
            if (vendor.phone) {
              updateData.phone = vendor.phone;
            }
            
            const { error } = await supabase
              .from('vendors')
              .update(updateData)
              .eq('id', existingVendor.id);

            if (error) {
              console.error('Error updating existing vendor:', error);
              throw error;
            }
            updatedVendors.push({ ...vendor, id: existingVendor.id });
          } else {
            // Only include fields that exist in the database schema
            const vendorData: any = {
              name: vendor.name,
              category: vendor.category,
              company_id: companyId
            };
            
            // Add optional fields only if they have values
            if (vendor.contact_name) {
              vendorData.contact_name = vendor.contact_name;
            }
            if (vendor.email) {
              vendorData.email = vendor.email;
            }
            if (vendor.phone) {
              vendorData.phone = vendor.phone;
            }
            
            vendorsToInsert.push(vendorData);
          }
        }

        // Insert truly new vendors
        if (vendorsToInsert.length > 0) {
          console.log('Inserting new vendors:', vendorsToInsert);

          const { data: insertedData, error: insertError } = await supabase
            .from('vendors')
            .insert(vendorsToInsert)
            .select();

          if (insertError) {
            console.error('Error inserting vendors:', insertError);
            throw insertError;
          }

          console.log('✅ Vendors inserted successfully:', insertedData);
          
          // Update local state with IDs to prevent future duplicates
          insertedData.forEach((inserted) => {
            const originalIndex = newVendors.findIndex(
              v => v.name === inserted.name && !v.id
            );
            if (originalIndex !== -1) {
              updatedVendors.push({ ...newVendors[originalIndex], id: inserted.id });
            }
          });
        }
      }

      // Update local state with IDs to prevent future duplicates
      setVendors(updatedVendors);

      // Note: Removed automatic deletion of vendors not in local state.
      // Vendors should be deleted explicitly via the delete button.
      // This prevents accidental deletion due to state sync issues.

      // Verify the save by reading back
      const { data: verifyData, error: verifyError } = await supabase
        .from('vendors')
        .select('id, name, category, contact_name, email, phone')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (verifyError) {
        console.error('Error verifying saved vendors:', verifyError);
      } else {
        console.log('🔍 Verification - Vendors in database:', verifyData);
      }

      toast({
        title: 'Vendors Saved',
        description: `${vendors.length} vendor(s) saved successfully`
      });

      // If external onNext is provided, use it instead of completeSection
      // This allows parent components to control navigation (e.g., in Phase 2 flows)
      if (externalOnNext) {
        await externalOnNext();
      } else {
        // Default Phase 1 behavior: complete section and navigate
        completeSection('vendors', {
          vendors: vendors.map(v => ({
            name: v.name,
            category: v.category,
            has_contact: !!(v.contact_name || v.email || v.phone),
          })),
          totalVendors: vendors.length,
          categories: [...new Set(vendors.map(v => v.category))],
        });
      }
    } catch (error: any) {
      console.error('❌ Error saving vendors:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
      });
      toast({
        title: 'Error',
        description: error.message || 'Failed to save vendors',
        variant: 'destructive'
      });
    }
  };

  const handleSave = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        toast({
          title: 'Error',
          description: 'Company ID not found. Please complete Business Profile first.',
          variant: 'destructive'
        });
        return;
      }

      // Save all vendors to database
      for (const vendor of vendors) {
        if (vendor.id) {
          // Update existing
          const { error: updateError } = await supabase
            .from('vendors')
            .update({
              name: vendor.name,
              category: vendor.category,
              contact_name: vendor.contact_name,
              email: vendor.email,
              phone: vendor.phone,
            })
            .eq('id', vendor.id);

          if (updateError) {
            console.error('Error updating vendor:', updateError);
            toast({
              title: 'Error',
              description: `Failed to update vendor: ${vendor.name}`,
              variant: 'destructive'
            });
            return;
          }
        } else {
          // Insert new
          const { data: insertedVendor, error: insertError } = await supabase
            .from('vendors')
            .insert({
              company_id: companyId,
              name: vendor.name,
              category: vendor.category,
              contact_name: vendor.contact_name,
              email: vendor.email,
              phone: vendor.phone,
            })
            .select()
            .single();

          if (insertError || !insertedVendor) {
            console.error('Error inserting vendor:', insertError);
            toast({
              title: 'Error',
              description: `Failed to save vendor: ${vendor.name}`,
              variant: 'destructive'
            });
            return;
          }

          // Update local state with ID
          const updatedVendors = vendors.map(v => 
            v === vendor ? { ...v, id: insertedVendor.id } : v
          );
          setVendors(updatedVendors);
        }
      }

      // Save to onboarding progress
      await updateSectionData('vendors', {
        vendors: vendors.map(v => ({
          name: v.name,
          category: v.category,
          contact_name: v.contact_name,
          email: v.email,
          phone: v.phone,
        })),
        totalVendors: vendors.length,
      });

      toast({
        title: 'Success',
        description: 'Vendors saved successfully',
      });
    } catch (error: any) {
      console.error('Error saving vendors:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save vendors',
        variant: 'destructive'
      });
    }
  };

  // Show loading state while data is being fetched
  if (loading) {
    return (
      <SectionWrapper
        title="Vendor Setup"
        description="Add your key vendors for accurate COGS and OPEX tracking. Start with COGS vendors (food suppliers), then OPEX vendors (utilities, services)."
        icon={<Store className="h-6 w-6" />}
        onNext={handleNext}
        onSave={handleSave}
        showSaveButton={true}
        onSkip={externalOnSkip || (() => skipSection('vendors'))}
        canSkip
        isNextDisabled={true}
      >
        <div className="space-y-6">
          {/* Add Vendor Button skeleton */}
          <div className="flex justify-center">
            <Skeleton className="h-10 w-36" />
          </div>

          {/* Vendors List skeleton */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-40" />
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-5 w-32" />
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </div>
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-4 w-48" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-8 w-8" />
                      <Skeleton className="h-8 w-8" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Vendor Setup"
      description="Add your key vendors for accurate COGS and OPEX tracking. Start with COGS vendors (food suppliers), then OPEX vendors (utilities, services)."
      icon={<Store className="h-6 w-6" />}
      onNext={handleNext}
      onSave={handleSave}
      showSaveButton={true}
      onSkip={externalOnSkip || (() => skipSection('vendors'))}
      canSkip
      isNextDisabled={vendors.length === 0}
    >
      <div className="space-y-6">
        {/* Add Vendor Button */}
        {!showForm && (
          <div className="flex justify-center">
            <Button onClick={() => setShowForm(true)} className="w-full md:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Add Vendor
            </Button>
          </div>
        )}

        {/* Add Vendor Form */}
        {showForm && (
          <Card>
            <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vendor-name">Vendor Name *</Label>
                <Input
                  id="vendor-name"
                  placeholder="e.g., Sysco Foods"
                  value={newVendor.name}
                  onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vendor-category">Category *</Label>
                <Select
                  value={showCustomCategoryInput || (newVendor.category && !['COGS', 'OPEX', 'Payroll', 'Rent'].includes(newVendor.category)) ? 'custom' : newVendor.category}
                  onValueChange={(value) => {
                    if (value === 'custom') {
                      setShowCustomCategoryInput(true);
                      setNewVendor({ ...newVendor, category: customCategory || '' });
                    } else {
                      setShowCustomCategoryInput(false);
                      setCustomCategory('');
                      setNewVendor({ ...newVendor, category: value });
                    }
                  }}
                >
                  <SelectTrigger id="vendor-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="COGS">COGS (Food & Beverage)</SelectItem>
                    <SelectItem value="OPEX">OPEX (Operations)</SelectItem>
                    <SelectItem value="Payroll">Payroll</SelectItem>
                    <SelectItem value="Rent">Rent</SelectItem>
                    <SelectItem value="custom">Custom Category</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(showCustomCategoryInput || (newVendor.category && !['COGS', 'OPEX', 'Payroll', 'Rent'].includes(newVendor.category))) && (
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="custom-category">Custom Category Name *</Label>
                  <Input
                    id="custom-category"
                    placeholder="Enter custom category name"
                    value={customCategory || newVendor.category || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomCategory(value);
                      setNewVendor({ ...newVendor, category: value });
                      // Keep the input visible when typing
                      if (!showCustomCategoryInput) {
                        setShowCustomCategoryInput(true);
                      }
                    }}
                    onBlur={() => {
                      // Only hide if empty, otherwise keep it visible
                      if (!customCategory?.trim() && !newVendor.category?.trim()) {
                        setShowCustomCategoryInput(false);
                        setNewVendor({ ...newVendor, category: 'COGS' });
                        setCustomCategory('');
                      }
                    }}
                    autoFocus
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="vendor-contact">Contact Person</Label>
                <Input
                  id="vendor-contact"
                  placeholder="Contact name"
                  value={newVendor.contact_name}
                  onChange={(e) => setNewVendor({ ...newVendor, contact_name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vendor-email">Email</Label>
                <Input
                  id="vendor-email"
                  type="email"
                  placeholder="vendor@example.com"
                  value={newVendor.email}
                  onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vendor-phone">Phone</Label>
                <Input
                  id="vendor-phone"
                  placeholder="(555) 123-4567"
                  value={newVendor.phone}
                  onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                />
              </div>

              <div className="flex items-end gap-2 md:col-span-2">
                <Button 
                  onClick={async () => {
                    await addVendor();
                    // Form will be hidden after successful add in addVendor function
                  }} 
                  className="flex-1"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Save Vendor
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowForm(false);
                    setNewVendor({
                      name: '',
                      category: 'COGS',
                      contact_name: '',
                      email: '',
                      phone: ''
                    });
                    setCustomCategory('');
                    setShowCustomCategoryInput(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Vendors List */}
        {vendors.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Added Vendors ({vendors.length})</h3>
            {vendors.map((vendor, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{vendor.name}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          vendor.category === 'COGS' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {vendor.category}
                        </span>
                      </div>
                      {vendor.contact_name && (
                        <p className="text-sm text-muted-foreground">Contact: {vendor.contact_name}</p>
                      )}
                      {vendor.email && (
                        <p className="text-sm text-muted-foreground">{vendor.email}</p>
                      )}
                      {vendor.phone && (
                        <p className="text-sm text-muted-foreground">{vendor.phone}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {vendor.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(vendor)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeVendor(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {vendors.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Store className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No vendors added yet</p>
            <p className="text-sm">Add your first vendor above to get started</p>
          </div>
        )}
      </div>

      {/* Edit Vendor Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Vendor</DialogTitle>
          </DialogHeader>
          {editingVendor && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-vendor-name">Vendor Name *</Label>
                <Input
                  id="edit-vendor-name"
                  placeholder="e.g., Sysco Foods"
                  value={editingVendor.name}
                  onChange={(e) => setEditingVendor({ ...editingVendor, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-vendor-category">Category *</Label>
                <Select
                  value={showEditCustomCategoryInput ? 'custom' : editingVendor.category}
                  onValueChange={(value) => {
                    if (value === 'custom') {
                      setShowEditCustomCategoryInput(true);
                      setEditingVendor({ ...editingVendor, category: '' });
                    } else {
                      setShowEditCustomCategoryInput(false);
                      setEditingCustomCategory('');
                      setEditingVendor({ ...editingVendor, category: value });
                    }
                  }}
                >
                  <SelectTrigger id="edit-vendor-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="COGS">COGS (Food & Beverage)</SelectItem>
                    <SelectItem value="OPEX">OPEX (Operations)</SelectItem>
                    <SelectItem value="Payroll">Payroll</SelectItem>
                    <SelectItem value="Rent">Rent</SelectItem>
                    <SelectItem value="custom">Custom Category</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(showEditCustomCategoryInput || (editingVendor.category && !['COGS', 'OPEX', 'Payroll', 'Rent'].includes(editingVendor.category))) && (
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="edit-custom-category">Custom Category Name *</Label>
                  <Input
                    id="edit-custom-category"
                    placeholder="Enter custom category name"
                    value={editingCustomCategory || editingVendor.category || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditingCustomCategory(value);
                      setEditingVendor({ ...editingVendor, category: value });
                      // Keep the input visible when typing
                      if (!showEditCustomCategoryInput) {
                        setShowEditCustomCategoryInput(true);
                      }
                    }}
                    onBlur={() => {
                      // Only hide if empty, otherwise keep it visible
                      if (!editingCustomCategory?.trim() && !editingVendor.category?.trim()) {
                        setShowEditCustomCategoryInput(false);
                        setEditingVendor({ ...editingVendor, category: 'COGS' });
                        setEditingCustomCategory('');
                      }
                    }}
                    autoFocus
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="edit-vendor-contact">Contact Person</Label>
                <Input
                  id="edit-vendor-contact"
                  placeholder="Contact name"
                  value={editingVendor.contact_name || ''}
                  onChange={(e) => setEditingVendor({ ...editingVendor, contact_name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-vendor-email">Email</Label>
                <Input
                  id="edit-vendor-email"
                  type="email"
                  placeholder="vendor@example.com"
                  value={editingVendor.email || ''}
                  onChange={(e) => setEditingVendor({ ...editingVendor, email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-vendor-phone">Phone</Label>
                <Input
                  id="edit-vendor-phone"
                  placeholder="(555) 123-4567"
                  value={editingVendor.phone || ''}
                  onChange={(e) => setEditingVendor({ ...editingVendor, phone: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button onClick={saveEditedVendor}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionWrapper>
  );
};
